import {
  DECODE_REQUEST,
  DECODE_SUCCESS,
  DECODE_FAILURE,
  UPDATE_HEX_VALUE,
  CLEAR_DECODER_STATE,
  TOGGLE_BIT,
  UPDATE_LOCAL_MATRIX,
  SET_DECODER_TYPE,
  INITIALIZE_WITH_DEFAULT,
  TLV_PARSE_REQUEST,
  TLV_PARSE_SUCCESS,
  TLV_PARSE_FAILURE,
  TLV_CLEAR_STATE,
  TLV_UPDATE_HEX_VALUE
} from "./actionTypes"

const getMinLengthByType = (decoderType) => {
  const lengths = {
    tvr: 10,
    cvmResults: 6,
    aip: 4,
    ctq: 4,
    appUsageControl: 4,
    termCap: 6,
    ttq: 8,
    tag9F40: 10,
    cvmList: 20
  }
  return lengths[decoderType]
}

const INIT_STATE = {
  isLoading: false,
  buttonTimer: false,
  
  decoderType: null,
  
  currentHexValue: "",
  dataLength: 0,
  showLengthBadge: false,
  
  hexValidationError: null,
  isValidHex: true,
  
  apiResponse: null,
  
  localMatrixData: null,
  
  error: null,
  
  builderData: null,
  
  responseCache: {
    tvr: null,
    aip: null,
    ctq: null,
    appUsageControl: null,
    termCap: null,
    cvmResults: null,
    ttq: null,
    tag9F40: null
  },
  
  // TLV specific state
  tlv: {
    isLoading: false,
    buttonTimer: false,
    currentHexValue: "",
    dataLength: 0,
    showLengthBadge: false,
    apiResponse: null,
    error: null,
    isCustomTlv: true
  }
}

const decoderReducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case DECODE_REQUEST:
      if (state.isLoading === true && 
          state.buttonTimer === true && 
          state.decoderType === action.payload.decoderType) {
        return state
      }
      
      return {
        ...state,
        isLoading: true,
        buttonTimer: true,
        error: null,
        decoderType: action.payload.decoderType
      }

    case DECODE_SUCCESS:
      const { response, decoderType } = action.payload
      
      if (state.apiResponse === response) {
        return state
      }
      
      return {
        ...state,
        isLoading: false,
        buttonTimer: false,
        apiResponse: response,
        localMatrixData: response.resultData?.matrixData?.bytes ? 
          JSON.parse(JSON.stringify(response.resultData.matrixData.bytes)) : null,
        builderData: response.resultData?.builder || null,
        error: null,
        responseCache: {
          ...state.responseCache,
          [decoderType]: response
        }
      }

    case DECODE_FAILURE:
      return {
        ...state,
        isLoading: false,
        buttonTimer: false,
        error: action.payload.error,
        apiResponse: null,
        localMatrixData: null,
        builderData: null
      }

    case UPDATE_HEX_VALUE:
      const hexValue = action.payload
      
      if (state.currentHexValue === hexValue) {
        return state
      }
      
      const hexLength = hexValue.length
      
      const minLength = getMinLengthByType(state.decoderType)
      const shouldClearMatrix = hexLength < minLength && hexLength > 0
      
      const newState = {
        ...state,
        currentHexValue: hexValue,
        dataLength: hexLength,
        showLengthBadge: hexLength > 0
      }
      
      if (shouldClearMatrix) {
        newState.localMatrixData = null
        newState.apiResponse = null
        newState.builderData = null
      }
      
      return newState

    case CLEAR_DECODER_STATE:
      return {
        ...INIT_STATE,
        responseCache: state.responseCache
      }

    case TOGGLE_BIT:
      return state

    case UPDATE_LOCAL_MATRIX:
      if (state.localMatrixData === action.payload) {
        return state
      }
      
      return {
        ...state,
        localMatrixData: action.payload
      }

    case SET_DECODER_TYPE:
      if (state.decoderType === action.payload) {
        return state
      }
      
      const newDecoderType = action.payload
      const cachedResponse = state.responseCache[newDecoderType]
      
      const baseState = {
        ...state,
        decoderType: newDecoderType
      }
      
      if (cachedResponse) {
        return {
          ...baseState,
          apiResponse: cachedResponse,
          localMatrixData: cachedResponse.resultData?.matrixData?.bytes ? 
            JSON.parse(JSON.stringify(cachedResponse.resultData.matrixData.bytes)) : null,
          builderData: cachedResponse.resultData?.builder || null
        }
      }
      
      return baseState

    case INITIALIZE_WITH_DEFAULT:
      if (state.isLoading === true && 
          state.buttonTimer === true && 
          state.decoderType === action.payload.decoderType) {
        return state
      }
      
      return {
        ...state,
        isLoading: true,
        buttonTimer: true,
        error: null,
        decoderType: action.payload.decoderType,
        currentHexValue: action.payload.data,
        dataLength: action.payload.data.length,
        showLengthBadge: true
      }

    // TLV specific cases
    case TLV_PARSE_REQUEST:
      return {
        ...state,
        tlv: {
          ...state.tlv,
          isLoading: true,
          buttonTimer: true,
          error: null,
          isCustomTlv: action.payload.isCustomTlv
        }
      }

    case TLV_PARSE_SUCCESS:
      return {
        ...state,
        tlv: {
          ...state.tlv,
          isLoading: false,
          buttonTimer: false,
          apiResponse: action.payload,
          error: null
        }
      }

    case TLV_PARSE_FAILURE:
      return {
        ...state,
        tlv: {
          ...state.tlv,
          isLoading: false,
          buttonTimer: false,
          error: action.payload,
          apiResponse: null
        }
      }

    case TLV_UPDATE_HEX_VALUE:
      const tlvHexValue = action.payload
      const tlvHexLength = tlvHexValue.length
      
      return {
        ...state,
        tlv: {
          ...state.tlv,
          currentHexValue: tlvHexValue,
          dataLength: tlvHexLength,
          showLengthBadge: tlvHexLength > 0
        }
      }

    case TLV_CLEAR_STATE:
      return {
        ...state,
        tlv: {
          isLoading: false,
          buttonTimer: false,
          currentHexValue: "",
          dataLength: 0,
          showLengthBadge: false,
          apiResponse: null,
          error: null,
          isCustomTlv: true
        }
      }

    default:
      return state
  }
}

export default decoderReducer
